import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { DesktopNav } from "@/components/NavDesktop";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import "@/App.css";

const SHOP_LOGO = "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png";

const NAV_LINKS = [
  { label: "Products", to: "/products" },
  { label: "Pricing", to: "/pricing" },
  { label: "Demo", to: "/demo" },
  { label: "Quote", to: "/quote" },
  { label: "Find a Shop", to: "/directory" },
  { label: "Claim Your Page", to: "/claim-your-page" },
];

export default function Navbar({ hideMobile }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <nav className={`glass-nav fixed top-0 left-0 right-0 z-50 ${hideMobile ? "max-lg:hidden" : ""}`} data-testid="navbar">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 flex items-center justify-between gap-4 h-16">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
          <div className="overflow-hidden" style={{ height: '40px', width: '160px' }}>
            <img src={SHOP_LOGO} alt="ShopAdvisor" className="w-full h-auto" style={{ marginTop: '-38%', marginBottom: '-38%' }} />
          </div>
        </Link>
        <DesktopNav user={user} logout={logout} links={NAV_LINKS} />
        {/* Mobile Menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger className="lg:hidden text-slate-700" data-testid="nav-mobile-menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </SheetTrigger>
          <SheetContent side="right" className="bg-white border-l border-slate-200 w-72 overflow-y-auto">
            <div className="flex flex-col gap-2 mt-8 pb-8">
              {NAV_LINKS.map(l => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2">
                  {l.label}
                </Link>
              ))}
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2">Admin</Link>
                  )}
                  {["admin", "sales_rep"].includes(user.role) && (
                    <Link to="/webadvisor" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2" data-testid="nav-mobile-webadvisor">Web Advisor</Link>
                  )}
                  {["admin", "sales_rep"].includes(user.role) && (
                    <Link to="/call-center" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2" data-testid="nav-mobile-call-center">Call Center</Link>
                  )}
                  {!["member", "applicant"].includes(user.role) && <Link to="/conference" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2">Meetings</Link>}
                  {user.role === "admin" ? (
                    <div className="grid grid-cols-2 gap-2 mt-4" data-testid="nav-mobile-dashboards">
                      {[["/admin", "Admin"], ["/dashboard", "Shop"], ["/sales", "Sales rep"], ["/webadvisor/leads", "Pipeline"]].map(([to, label]) => (
                        <Link key={to} to={to} onClick={() => setOpen(false)} className="bg-blue-600 text-white text-center py-3 rounded-lg font-medium text-sm" data-testid={`nav-mobile-dash-${label.replace(" ", "-").toLowerCase()}`}>{label}</Link>
                      ))}
                    </div>
                  ) : user.role === "member" ? <Link to="/client/dashboard" onClick={() => setOpen(false)} className="bg-blue-600 text-white text-center py-3 rounded-lg font-medium mt-4" data-testid="nav-mobile-client-portal">My business</Link> : (
                    <Link to={({ sales_rep: "/sales", referral_agent: "/agent", applicant: "/careers/portal", demo: "/demo/app" }[user.role] || "/dashboard")} onClick={() => setOpen(false)} className="bg-blue-600 text-white text-center py-3 rounded-lg font-medium mt-4">{user.role === "applicant" ? "My application" : "Dashboard"}</Link>
                  )}
                  {["admin", "sales_rep"].includes(user.role) && <Link to="/sales/dashboard" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2" data-testid="nav-mobile-sales-dash">Sales dashboard</Link>}
                  {["admin", "sales_rep"].includes(user.role) && <Link to="/academy" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2" data-testid="nav-mobile-academy">Academy</Link>}
                  <Link to="/profile" onClick={() => setOpen(false)} className="text-slate-700 hover:text-blue-600 text-lg font-medium py-2" data-testid="nav-mobile-profile">My profile</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="border border-slate-300 text-slate-700 py-3 rounded-lg font-medium">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="border border-slate-300 text-slate-700 text-center py-3 rounded-lg font-medium mt-4">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="bg-blue-600 text-white text-center py-3 rounded-lg font-bold">Get Started</Link>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
